/* OpenFileModal — pick an existing SharePoint Office file to open in
   the Daedalus artifact panel.
 *
 * Flow:
 *   1. Pick a site (and, on writable sites, optionally jump to a
 *      project's folder).
 *   2. Navigate folders by clicking rows; climb back via the breadcrumb
 *      or the up-chevron.
 *   3. The modal calls /tools/documents/list-sharepoint and renders
 *      the file list.
 *   4. Click a file row → emit an ArtifactRef the parent (Sidebar) pushes
 *      onto HomeChat's openArtifacts. The panel then runs the
 *      embed flow automatically.
 *
 * Visual language mirrors the bids FilterPanel + composer "+" menu
 * (rounded card, soft shadow, drill-in option rows, breadcrumb header)
 * so the SharePoint picker reads as part of the same system rather than
 * a legacy squared modal.
 */

import { useEffect, useState } from 'react';
import { I } from '@/components/brand';
import {
  listMyProjects,
  listSharepoint,
  listSites,
  type ProjectRow,
  type SharepointEntry,
  type SharepointSite,
} from '@/api/client';
import type { ArtifactRef } from '@/api/schemas';
import { useAuth } from '@/auth/MsalBootstrap';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Parent pushes the returned artifact onto its open-artifacts list,
   *  closes the modal, and the panel renders the embed. */
  onPick: (artifact: ArtifactRef) => void;
  /** Optional case-insensitive file-extension allowlist (e.g. ['.pdf'])
   *  used by the previz Tools tile to constrain the picker to drawings.
   *  Folders are always shown unconditionally so the user can
   *  navigate. When unset (Sidebar's call), all file types render. */
  acceptedExtensions?: string[];
  /** Optional starting subfolder seeded on each open (cleared with the
   *  rest of the modal state on close). Used by previz to pre-position
   *  inside ``Drawings`` / ``Plans`` when the operator knows the
   *  convention. Default: empty (drive root). */
  initialSubfolder?: string;
  /** Optional header title override. The Tools-tile callers use this
   *  to read "Pick the drawings PDF" instead of the generic Sidebar
   *  copy. Default: "Open file from SharePoint". */
  title?: string;
}

export function OpenFileModal({
  open,
  onClose,
  onPick,
  acceptedExtensions,
  initialSubfolder,
  title,
}: Props) {
  const { getAccessToken } = useAuth();
  // Configured sites — fetched once when the modal opens. The first
  // entry (typically the dedicated upload drive) is selected by
  // default; the user can switch to any read-site to browse their
  // existing project libraries.
  const [sites, setSites] = useState<SharepointSite[]>([]);
  const [siteDriveId, setSiteDriveId] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [subfolder, setSubfolder] = useState(initialSubfolder ?? '');
  const [entries, setEntries] = useState<SharepointEntry[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when the modal opens so a stale prior session doesn't
  // peek through. Loads sites + projects in parallel.
  useEffect(() => {
    if (!open) {
      setEntries([]);
      setError(null);
      return;
    }
    // Seed the subfolder from initialSubfolder when the modal opens.
    // Without this, navigating into a subfolder on one open and then
    // closing/reopening would keep the stale subfolder. Doing it here
    // (on open == true) preserves the prop's intent as a "starting
    // point, not a clamp".
    setSubfolder(initialSubfolder ?? '');
    let cancelled = false;
    setLoadingProjects(true);
    (async () => {
      try {
        const token = await getAccessToken();
        const [siteList, projectList] = await Promise.all([
          listSites(token).catch(() => [] as SharepointSite[]),
          listMyProjects(token),
        ]);
        if (cancelled) return;
        setSites(siteList);
        setSiteDriveId(siteList[0]?.drive_id ?? null);
        setProjects(projectList);
        // Default to the first project so the user sees a file list
        // immediately rather than an empty dropdown.
        const first = projectList[0];
        const firstId = first
          ? Number(first.project_id ?? first.id ?? 0) || null
          : null;
        setProjectId(firstId);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load projects'
          );
        }
      } finally {
        if (!cancelled) setLoadingProjects(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, getAccessToken]);

  // Re-fetch the file list whenever site / project / subfolder change.
  // Two modes:
  // All sites browse via drive_id + subfolder. The Project dropdown
  // on writable sites is a CONVENIENCE that pre-fills the subfolder
  // as ``Projects/{project_name}`` — picking a project is no longer
  // required, so the writable Daedalus site can be used as a free-
  // form sandbox just like the read-only sites.
  useEffect(() => {
    if (!open) return;
    const activeSite = sites.find((s) => s.drive_id === siteDriveId) ?? null;
    if (!activeSite) return;
    let cancelled = false;
    setLoadingFiles(true);
    setError(null);
    (async () => {
      try {
        const token = await getAccessToken();
        const list = await listSharepoint(token, {
          drive_id: activeSite.drive_id,
          subfolder: subfolder.trim() || undefined,
        });
        if (!cancelled) setEntries(list);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to list files'
          );
          setEntries([]);
        }
      } finally {
        if (!cancelled) setLoadingFiles(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sites, siteDriveId, subfolder, getAccessToken]);

  if (!open) return null;

  // Apply the case-insensitive extension allowlist when provided.
  // Folders are always shown — the user needs to navigate freely
  // even when constrained to a single file type.
  const visibleEntries = (() => {
    if (!acceptedExtensions || acceptedExtensions.length === 0) return entries;
    const lowered = acceptedExtensions.map((e) =>
      (e.startsWith('.') ? e : `.${e}`).toLowerCase(),
    );
    return entries.filter((e) => {
      if (e.kind === 'folder') return true;
      const name = e.name.toLowerCase();
      return lowered.some((ext) => name.endsWith(ext));
    });
  })();
  const resolvedTitle = title ?? 'Open file from SharePoint';

  const activeSite = sites.find((s) => s.drive_id === siteDriveId) ?? null;
  const writable = !!activeSite?.writable;
  // Breadcrumb segments for the current path. The leading crumb is the
  // site itself (clears the subfolder); each segment truncates the path
  // back to that depth.
  const pathSegments = subfolder
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean);

  function projectLabel(p: ProjectRow): string {
    return (
      (p.project_name as string) ??
      (p.name as string) ??
      `Project ${p.project_id ?? p.id ?? ''}`
    );
  }

  function projectIdOf(p: ProjectRow): number {
    return Number(p.project_id ?? p.id ?? 0);
  }

  // Truncate the path to the first `depth` segments (0 == site root).
  function gotoDepth(depth: number) {
    setSubfolder(pathSegments.slice(0, depth).join('/'));
  }

  function goUp() {
    setSubfolder(pathSegments.slice(0, -1).join('/'));
  }

  function pick(entry: SharepointEntry) {
    if (entry.kind === 'folder') {
      // Folder click descends.
      const trimmed = subfolder.trim().replace(/\/$/, '');
      setSubfolder(trimmed ? `${trimmed}/${entry.name}` : entry.name);
      return;
    }
    const artifact: ArtifactRef = {
      run_id: null,
      drive_id: entry.drive_id,
      drive_item_id: entry.drive_item_id,
      tool: 'open_file_modal',
      document_type_key: null,
      document_title: entry.name,
      output_format: entry.output_format,
      status: 'opened',
      file_url: entry.web_url,
      sharepoint_path: null,
      embed_url: null,
      last_tool: 'open_file_modal',
    };
    onPick(artifact);
    onClose();
  }

  return (
    <div
      className="ofm-back"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ofm-title"
      onClick={onClose}
    >
      <div className="ofm-card" onClick={(e) => e.stopPropagation()}>
        <header className="ofm-head">
          <div id="ofm-title" className="ofm-title">{resolvedTitle}</div>
          <button type="button" className="ofm-close" onClick={onClose} aria-label="Close">
            <I.close width={16} height={16} />
          </button>
        </header>

        <div className="ofm-controls">
          {sites.length > 0 && (
            <label className="ofm-field">
              <span className="ofm-field-lbl">site</span>
              <div className="ofm-select">
                <select
                  value={siteDriveId ?? ''}
                  onChange={(e) => {
                    setSiteDriveId(e.target.value || null);
                    // Reset subfolder when switching sites — the prior
                    // path won't be valid in the new drive.
                    setSubfolder('');
                  }}
                >
                  {sites.map((s) => (
                    <option key={s.drive_id} value={s.drive_id}>
                      {s.label}{s.writable ? '' : ' (read-only)'}
                    </option>
                  ))}
                </select>
                <span className="ofm-select-chev" aria-hidden>
                  <I.chevR width={13} height={13} />
                </span>
              </div>
            </label>
          )}
          {writable && projects.length > 0 && (
            <label className="ofm-field" title="Jump to a project's folder">
              <span className="ofm-field-lbl">jump to project</span>
              <div className="ofm-select">
                <select
                  value={projectId ?? ''}
                  disabled={loadingProjects}
                  onChange={(e) => {
                    const id = Number(e.target.value) || null;
                    setProjectId(id);
                    if (id) {
                      const p = projects.find((x) => projectIdOf(x) === id);
                      const label = (p && projectLabel(p)) || '';
                      if (label) {
                        // Pre-fill the subfolder so the list-sharepoint
                        // call walks into Projects/{name}. Matches the
                        // path Daedalus's own uploads land under.
                        setSubfolder(`Projects/${label.replace(/ /g, '_')}`);
                      }
                    }
                  }}
                >
                  <option value="">— (browse root) —</option>
                  {projects.map((p) => (
                    <option key={projectIdOf(p)} value={projectIdOf(p)}>
                      {projectLabel(p)}
                    </option>
                  ))}
                </select>
                <span className="ofm-select-chev" aria-hidden>
                  <I.chevR width={13} height={13} />
                </span>
              </div>
            </label>
          )}
        </div>

        {/* Breadcrumb path nav — replaces the old free-text subfolder
            input + "↑ up" button. The up-chevron climbs one level; each
            crumb jumps straight to that depth. */}
        <div className="ofm-crumbs">
          <button
            type="button"
            className="ofm-up"
            onClick={goUp}
            disabled={pathSegments.length === 0}
            aria-label="Up one folder"
          >
            <I.chevL width={14} height={14} />
          </button>
          <nav className="ofm-crumb-trail" aria-label="Folder path">
            <button
              type="button"
              className="ofm-crumb"
              onClick={() => gotoDepth(0)}
            >
              {activeSite?.label ?? 'root'}
            </button>
            {pathSegments.map((seg, i) => (
              <span key={`${seg}-${i}`} className="ofm-crumb-grp">
                <span className="ofm-crumb-sep" aria-hidden>
                  <I.chevR width={11} height={11} />
                </span>
                <button
                  type="button"
                  className="ofm-crumb"
                  onClick={() => gotoDepth(i + 1)}
                >
                  {seg}
                </button>
              </span>
            ))}
          </nav>
        </div>

        <div className="ofm-list">
          {error && <div className="ofm-state ofm-err">{error}</div>}
          {!error && loadingFiles && <div className="ofm-state">Loading files…</div>}
          {!error && !loadingFiles && visibleEntries.length === 0 && (
            <div className="ofm-state">
              {acceptedExtensions && acceptedExtensions.length > 0 && entries.length > 0
                ? `No ${acceptedExtensions.join(' / ')} files in this folder.`
                : 'No files here.'}
            </div>
          )}
          {!error && !loadingFiles && visibleEntries.length > 0 && (
            <ul className="ofm-rows">
              {visibleEntries.map((e) => {
                const isFolder = e.kind === 'folder';
                return (
                  <li key={e.drive_item_id}>
                    <button
                      type="button"
                      className={`ofm-opt ${isFolder ? 'is-folder' : ''}`}
                      onClick={() => pick(e)}
                    >
                      <span className="ofm-opt-ic" aria-hidden>
                        {isFolder ? <I.folder width={16} height={16} /> : <I.doc width={16} height={16} />}
                      </span>
                      <span className="ofm-opt-name" title={e.name}>{e.name}</span>
                      {isFolder ? (
                        <span className="ofm-opt-chev" aria-hidden>
                          <I.chevR width={13} height={13} />
                        </span>
                      ) : (
                        e.output_format && (
                          <span className="ofm-opt-ext">{e.output_format}</span>
                        )
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <style>{`
        .ofm-back {
          position: fixed; inset: 0; z-index: 100;
          background: rgba(0, 0, 0, 0.5);
          display: flex; align-items: center; justify-content: center;
          padding: 32px;
          animation: ofmBackIn 140ms ease;
        }
        @keyframes ofmBackIn { from { opacity: 0; } to { opacity: 1; } }

        /* Rounded card + soft shadow, matched to .fp-bubble / .pm-bubble so
           the picker reads as part of the same menu system. overflow:hidden
           clips the header rule + list scroll to the rounded corners. */
        .ofm-card {
          width: 560px; max-width: 100%;
          max-height: 80vh; display: flex; flex-direction: column;
          background: var(--bg-2);
          border: 1px solid var(--rule-soft);
          border-radius: 16px;
          box-shadow: 0 18px 48px rgba(0, 0, 0, 0.22), 0 2px 6px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          animation: ofmCardIn 240ms cubic-bezier(.34, 1.56, .64, 1);
        }
        @keyframes ofmCardIn {
          from { opacity: 0; transform: translateY(8px) scale(.98); }
          to   { opacity: 1; transform: none; }
        }

        .ofm-head {
          display: flex; justify-content: space-between; align-items: center;
          padding: 14px 18px; border-bottom: 1px solid var(--rule-hair);
        }
        .ofm-title {
          font-family: 'IBM Plex Serif', Georgia, serif;
          font-size: 18px;
        }
        .ofm-close {
          display: inline-flex; align-items: center; justify-content: center;
          width: 28px; height: 28px; border: 0; border-radius: 8px;
          background: transparent; color: var(--ink-3); cursor: pointer;
          transition: background 120ms ease, color 120ms ease;
        }
        .ofm-close:hover { background: var(--bg-1); color: var(--accent); }

        .ofm-controls {
          display: flex; flex-wrap: wrap; gap: 10px; align-items: end;
          padding: 12px 18px 10px;
        }
        /* Flexible, shrinkable fields. min-width:0 on the field + the
           select keeps a long project name from sizing the row to its
           content and blowing past the card; fields wrap instead. */
        .ofm-field { display: flex; flex-direction: column; gap: 5px; flex: 1 1 200px; min-width: 0; }
        .ofm-field-lbl {
          font-family: 'JetBrains Mono', monospace; font-size: 10px;
          letter-spacing: 0.06em; text-transform: uppercase; color: var(--ink-3);
        }
        /* Custom-chevron select — native arrow removed, our chevR rotated
           to a down caret so it matches the brand line-icon set. */
        .ofm-select { position: relative; display: flex; min-width: 0; }
        .ofm-select select {
          width: 100%; min-width: 0;
          appearance: none; -webkit-appearance: none;
          padding: 8px 30px 8px 12px;
          background: var(--bg-1); color: var(--ink);
          border: 1px solid var(--rule-soft); border-radius: 10px;
          font: inherit; font-size: 13px; outline: 0; cursor: pointer;
          text-overflow: ellipsis;
        }
        .ofm-select select:focus { border-color: var(--accent); }
        .ofm-select select:disabled { opacity: 0.55; cursor: default; }
        .ofm-select-chev {
          position: absolute; right: 11px; top: 50%;
          transform: translateY(-50%) rotate(90deg);
          display: flex; color: var(--ink-3); pointer-events: none;
        }

        .ofm-crumbs {
          display: flex; align-items: center; gap: 8px;
          padding: 2px 18px 12px;
          border-bottom: 1px solid var(--rule-hair);
        }
        .ofm-up {
          display: inline-flex; align-items: center; justify-content: center;
          width: 28px; height: 28px; flex-shrink: 0;
          border: 1px solid var(--rule-soft); border-radius: 8px;
          background: transparent; color: var(--ink-2); cursor: pointer;
          transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
        }
        .ofm-up:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); }
        .ofm-up:disabled { opacity: 0.4; cursor: default; }
        .ofm-crumb-trail {
          display: flex; align-items: center; gap: 1px; min-width: 0;
          overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none;
        }
        .ofm-crumb-trail::-webkit-scrollbar { display: none; }
        .ofm-crumb-grp { display: inline-flex; align-items: center; flex-shrink: 0; }
        .ofm-crumb {
          flex-shrink: 0;
          background: transparent; border: 0; padding: 4px 7px; border-radius: 6px;
          font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.02em;
          color: var(--ink-3); cursor: pointer; white-space: nowrap;
          max-width: 180px; overflow: hidden; text-overflow: ellipsis;
          transition: background 120ms ease, color 120ms ease;
        }
        .ofm-crumb:hover { background: var(--bg-1); color: var(--accent); }
        /* The trailing crumb is the folder you're in — give it ink weight. */
        .ofm-crumb-grp:last-child .ofm-crumb { color: var(--ink); }
        .ofm-crumb-sep { display: flex; color: var(--ink-4); flex-shrink: 0; }

        .ofm-list { flex: 1; min-height: 0; overflow: auto; padding: 6px; }
        .ofm-state { padding: 28px; color: var(--ink-3); font-size: 13px; text-align: center; }
        .ofm-err { color: var(--danger); }
        .ofm-rows { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
        /* Option rows mirror .fp-opt / .pm-opt: rounded, accent-on-hover,
           a chevron for folders (drill) and a format pill for files. */
        .ofm-opt {
          display: flex; gap: 10px; align-items: center;
          width: 100%; padding: 9px 12px;
          background: transparent; border: 0; border-radius: 9px;
          font: inherit; font-size: 13px; color: var(--ink);
          text-align: left; cursor: pointer;
          transition: background 120ms ease, color 120ms ease;
        }
        .ofm-opt:hover { background: var(--bg-1); color: var(--accent); }
        .ofm-opt-ic { display: flex; flex-shrink: 0; color: var(--ink-3); }
        .ofm-opt.is-folder .ofm-opt-ic { color: var(--accent); }
        .ofm-opt:hover .ofm-opt-ic { color: var(--accent); }
        .ofm-opt-name {
          flex: 1; min-width: 0;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .ofm-opt-chev { display: flex; color: var(--ink-4); flex-shrink: 0; }
        .ofm-opt:hover .ofm-opt-chev { color: var(--accent); }
        .ofm-opt-ext {
          flex-shrink: 0;
          font-family: 'JetBrains Mono', monospace; font-size: 10px;
          letter-spacing: 0.06em; text-transform: uppercase;
          padding: 1px 7px; border: 1px solid var(--rule-hair); border-radius: 999px;
          color: var(--ink-3);
        }
        .ofm-opt:hover .ofm-opt-ext { color: var(--accent); border-color: var(--accent); }

        /* Touch: real hit sizes for the header ✕ and the file rows
           (header vertical padding shrinks so the bigger ✕ doesn't
           balloon the header). */
        @media (pointer: coarse) {
          .ofm-close { width: 44px; height: 44px; }
          .ofm-head { padding: 6px 10px 6px 18px; }
          .ofm-opt { min-height: 40px; }
          .ofm-up { width: 40px; height: 40px; }
        }
        /* Phone: the 32px gutter wastes a third of a 390px screen, and
           100vh overshoots under iOS Safari's collapsing chrome — cap
           with dvh (vh line is the fallback for older engines). */
        @media (max-width: 480px) {
          .ofm-back { padding: 12px 8px; }
          .ofm-card {
            width: 100%;
            max-height: calc(100vh - 24px);
            max-height: calc(100dvh - 24px);
          }
        }
      `}</style>
    </div>
  );
}
